import { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import { LogOut, Lock, Mail, Phone, Upload, Link2, Download, FileArchive, ShieldCheck, RefreshCw } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Nav";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const inputCls = "bg-[#070F1E] border-[#0066FF]/30 text-white placeholder:text-slate-600 focus-visible:ring-[#0066FF]";

function Login({ onLogin }) {
  const [email, setEmail] = useState("admin@portbridgeglobal.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      toast.success("Welcome back");
      onLogin(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#070F1E] px-5">
      <form onSubmit={submit} className="w-full max-w-md rounded-3xl border border-[#0066FF]/25 bg-[#0B172A] p-8 sm:p-10">
        <div className="flex justify-center">
          <Logo className="h-16" />
        </div>
        <h1 className="mt-8 text-center font-display text-xl font-bold text-white">Admin Portal</h1>
        <p className="mt-1 text-center text-sm text-slate-500">Restricted to PORTBRIDGE Global staff</p>

        <div className="mt-8 space-y-4">
          <div>
            <Label className="text-slate-400">Email</Label>
            <Input
              data-testid="admin-login-username-input"
              type="email"
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-slate-400">Password</Label>
            <Input
              data-testid="admin-login-password-input"
              type="password"
              className={inputCls}
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <Button
            data-testid="admin-login-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full rounded-xl bg-[#0066FF] py-3 font-semibold hover:bg-[#0052cc]"
          >
            {busy ? "Signing in..." : "Sign In"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function ContactTab({ settings, reload }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (settings) {
      setEmail(settings.contact_email || "");
      setPhone(settings.contact_phone || "");
    }
  }, [settings]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", { contact_email: email, contact_phone: phone });
      toast.success("Contact info updated on the website");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <Label className="flex items-center gap-2 text-slate-400"><Mail className="h-4 w-4" /> Contact email shown on the website</Label>
        <Input data-testid="admin-contact-email-input" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div>
        <Label className="flex items-center gap-2 text-slate-400"><Phone className="h-4 w-4" /> Contact phone shown on the website</Label>
        <Input data-testid="admin-contact-phone-input" className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value)} />
      </div>
      <Button data-testid="admin-save-contact-btn" onClick={save} disabled={busy} className="rounded-xl bg-[#0066FF] font-semibold hover:bg-[#0052cc]">
        {busy ? "Saving..." : "Save Contact Info"}
      </Button>
    </div>
  );
}

function SecurityTab() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!current || !next) return toast.error("Fill in all password fields");
    if (next !== confirm) return toast.error("New passwords do not match");
    if (next.length < 5) return toast.error("New password must be at least 5 characters");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed successfully");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <Label className="text-slate-400">Current password</Label>
        <Input data-testid="admin-current-password-input" type="password" className={inputCls} value={current} onChange={(e) => setCurrent(e.target.value)} />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label className="text-slate-400">New password</Label>
          <Input data-testid="admin-new-password-input" type="password" className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} />
        </div>
        <div>
          <Label className="text-slate-400">Confirm new password</Label>
          <Input data-testid="admin-confirm-password-input" type="password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
      </div>
      <Button data-testid="admin-password-change-submit" onClick={save} disabled={busy} className="flex items-center gap-2 rounded-xl bg-[#0066FF] font-semibold hover:bg-[#0052cc]">
        <Lock className="h-4 w-4" /> {busy ? "Updating..." : "Change Password"}
      </Button>
    </div>
  );
}

function PortalTab({ settings, reload }) {
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setUrl(settings?.portal?.type === "url" ? settings.portal.url || "" : "");
  }, [settings]);

  const upload = async () => {
    if (!file) return toast.error("Choose a file first");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/portal/upload", fd);
      toast.success("Setup file published — the website download button is now live");
      setFile(null);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveUrl = async () => {
    setBusy(true);
    try {
      await api.put("/settings", { portal_url: url.trim() });
      toast.success(url.trim() ? "Download URL saved" : "Download URL cleared");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const portal = settings?.portal;

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-[#0066FF]/25 bg-[#070F1E] p-5">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Current portal setup</p>
        {portal?.type === "file" ? (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <FileArchive className="h-5 w-5 text-[#00F0FF]" />
            <span className="text-sm font-semibold text-white" data-testid="admin-current-file-name">{portal.filename}</span>
            {portal.size != null && <span className="text-xs text-slate-500">{(portal.size / (1024 * 1024)).toFixed(2)} MB</span>}
            <a
              data-testid="admin-test-download-link"
              href={`${API}/portal/download`}
              className="ml-auto flex items-center gap-2 rounded-full border border-[#0066FF]/40 px-4 py-1.5 text-xs font-semibold text-[#00F0FF] hover:bg-[#0066FF]/10"
            >
              <Download className="h-3.5 w-3.5" /> Test download
            </a>
          </div>
        ) : portal?.type === "url" ? (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Link2 className="h-5 w-5 text-[#00F0FF]" />
            <a href={portal.url} target="_blank" rel="noopener noreferrer" className="truncate text-sm font-semibold text-white underline decoration-[#0066FF]/50">
              {portal.url}
            </a>
          </div>
        ) : (
          <p className="mt-2 text-sm text-slate-500">No setup published yet — the website download button will show "coming soon".</p>
        )}
      </div>

      <div className="rounded-2xl border border-[#00F0FF]/25 bg-[#070F1E] p-5">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Direct download link — share with carriers</p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <code className="text-sm font-semibold break-all text-white" data-testid="admin-direct-link-value">{window.location.origin}/download</code>
          <Button
            data-testid="admin-copy-direct-link-btn"
            onClick={async () => {
              await navigator.clipboard.writeText(`${window.location.origin}/download`);
              toast.success("Direct download link copied — share it with your carriers");
            }}
            variant="outline"
            className="ml-auto shrink-0 rounded-xl border-[#0066FF]/40 font-semibold text-[#00F0FF] hover:bg-[#0066FF]/10 hover:text-[#00F0FF]"
          >
            <Link2 className="h-4 w-4" /> Copy Link
          </Button>
        </div>
        <p className="mt-2 text-xs text-slate-500">Anyone opening this link gets the installer downloaded automatically — works for whichever setup (file or URL) is published.</p>
      </div>

      <div>
        <Label className="flex items-center gap-2 text-slate-400"><Upload className="h-4 w-4" /> Upload a new setup file — any type (.exe, .msi, .zip, .7z, …)</Label>
        <div className="mt-2 flex flex-col gap-3 sm:flex-row">
          <Input
            data-testid="admin-portal-file-upload-input"
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className={`${inputCls} file:mr-3 file:rounded-lg file:border-0 file:bg-[#0066FF] file:px-3 file:py-1 file:text-xs file:font-semibold file:text-white`}
          />
          <Button data-testid="admin-save-setup-btn" onClick={upload} disabled={busy} className="shrink-0 rounded-xl bg-[#0066FF] font-semibold hover:bg-[#0052cc]">
            {busy ? "Uploading..." : "Upload & Publish"}
          </Button>
        </div>
      </div>

      <div>
        <Label className="flex items-center gap-2 text-slate-400"><Link2 className="h-4 w-4" /> …or paste an external download URL (used when no file is uploaded)</Label>
        <div className="mt-2 flex flex-col gap-3 sm:flex-row">
          <Input data-testid="admin-portal-url-input" className={inputCls} placeholder="https://your-link.com/setup.exe" value={url} onChange={(e) => setUrl(e.target.value)} />
          <Button data-testid="admin-save-url-btn" onClick={saveUrl} disabled={busy} variant="outline" className="shrink-0 rounded-xl border-[#0066FF]/40 font-semibold text-[#00F0FF] hover:bg-[#0066FF]/10 hover:text-[#00F0FF]">
            Save URL
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [settings, setSettings] = useState(null);

  const loadSettings = () => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        loadSettings();
      })
      .catch(() => setUser(null));
  }, []);

  const signOut = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#070F1E]">
        <RefreshCw className="h-6 w-6 animate-spin text-[#0066FF]" />
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Login onLogin={(u) => { setUser(u); loadSettings(); }} />
        <Toaster position="top-center" richColors theme="dark" />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#070F1E] px-5 py-8 text-slate-100 lg:px-10">
      <div className="mx-auto max-w-4xl">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Logo className="h-12" />
            <div>
              <h1 className="font-display text-lg font-bold text-white">Admin Portal</h1>
              <p className="text-xs text-slate-500">{user.email}</p>
            </div>
          </div>
          <Button data-testid="admin-signout-btn" onClick={signOut} variant="outline" className="flex items-center gap-2 rounded-xl border-[#0066FF]/40 text-slate-300 hover:bg-[#0066FF]/10 hover:text-white">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </header>

        <div className="mt-8 rounded-3xl border border-[#0066FF]/25 bg-[#0B172A] p-6 sm:p-8">
          <Tabs defaultValue="contact">
            <TabsList className="bg-[#070F1E] border border-[#0066FF]/25">
              <TabsTrigger data-testid="admin-tab-contact" value="contact" className="gap-2 data-[state=active]:bg-[#0066FF] data-[state=active]:text-white"><Mail className="h-4 w-4" /> Contact Info</TabsTrigger>
              <TabsTrigger data-testid="admin-tab-security" value="security" className="gap-2 data-[state=active]:bg-[#0066FF] data-[state=active]:text-white"><Lock className="h-4 w-4" /> Password</TabsTrigger>
              <TabsTrigger data-testid="admin-tab-portal" value="portal" className="gap-2 data-[state=active]:bg-[#0066FF] data-[state=active]:text-white"><ShieldCheck className="h-4 w-4" /> Portal Setup</TabsTrigger>
            </TabsList>
            <TabsContent value="contact" className="mt-6"><ContactTab settings={settings} reload={loadSettings} /></TabsContent>
            <TabsContent value="security" className="mt-6"><SecurityTab /></TabsContent>
            <TabsContent value="portal" className="mt-6"><PortalTab settings={settings} reload={loadSettings} /></TabsContent>
          </Tabs>
        </div>
      </div>
      <Toaster position="top-center" richColors theme="dark" />
    </div>
  );
}
